import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { isFlowActive } from "../game/flow";
import { resolveMove, type MoveResolution } from "../game/helpers";
import { createInstantPressHandlers } from "../lib/instantPress";
import type { BoardTile, ScoreEventType } from "../game/types";
import { getTileColor, isDangerTile, moveHintFromOutcome, type MoveHint, type Theme } from "../theme";

const CLEAR_OUTCOMES: MoveResolution["outcome"][] = [
  "match_clear",
  "build_to_ten_clear",
  "negative_clear"
];
const PARTICLE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const INCREMENT_RAY_ANGLES = [0, 60, 120, 180, 240, 300];
const SCORE_POP_LANE_OFFSETS = [-12, 0, 12] as const;
const FLOW_PULSE_DURATION_MS = 220;
const BUILD_MULTIPLIER_PULSE_DURATION_MS = 460;
const TILE_MOTION_TRANSITION = {
  duration: 0.16,
  ease: [0.22, 1, 0.36, 1] as const
};
const BOARD_STEP_SMALL = 68;
const BOARD_STEP_LARGE = 80;
const INCREMENT_SPOTLIGHT_MAX_WIDTH = 240;
const INCREMENT_SPOTLIGHT_VIEWPORT_GUTTER = 32;

export type PendingMove = {
  boardIndex: number;
  outcome: MoveResolution["outcome"];
  particleColor: string;
} | null;

export type FloatingScore = {
  id: string;
  value: number;
  kind: ScoreEventType;
  lane: number;
  delayMs: number;
  origin:
    | {
        kind: "tile";
        boardIndex: number;
        boardSize: number;
      }
    | {
        kind: "board_center";
      };
};

export type BoardIncrementLane = {
  laneIndex: number;
  tileId: string;
  expiresAt: number;
  durationMs: number;
  burstCount: number;
};

export type BoardTimerDisplay =
  | {
      kind: "flow";
      expiresAt: number;
      durationMs: number;
      flowCount: number;
    }
  | {
      kind: "refill";
      expiresAt: number | null;
      durationMs: number;
    };

type TimerSpotlightKind = "flow" | "refill";

type BoardProps = {
  board: BoardTile[];
  flowCount: number;
  incrementLanes: BoardIncrementLane[];
  timerDisplay: BoardTimerDisplay | null;
  timerPausedAt: number | null;
  timerSpotlightKind: TimerSpotlightKind | null;
  incrementSpotlightTileId: string | null;
  floatingScores: FloatingScore[];
  boardTopOverlay?: ReactNode;
  boardRowOverlay?: ReactNode;
  selectedHandValue: number | null;
  spotlightTileId?: string | null;
  dimNonSpotlightTiles?: boolean;
  dimAllTiles?: boolean;
  showMoveHints?: boolean;
  stabilizeAnimations?: boolean;
  gameOver: boolean;
  isResolvingMove: boolean;
  pendingMove: PendingMove;
  theme: Theme;
  onSelectTile: (index: number) => void;
  onDismissTimerSpotlight: () => void;
};

type TilePreview = {
  label: string;
  kind: "match" | "build" | "ten" | "negative" | "zero";
  risky: boolean;
  ariaLabel: string;
};

export function getTileBuildMultiplier(tile: BoardTile): number {
  return Math.max(1, tile.buildDepth + 1);
}

export function getTileBuildMultiplierLabel(tile: BoardTile): string | null {
  const buildMultiplier = getTileBuildMultiplier(tile);
  return buildMultiplier > 1 ? `x${buildMultiplier}` : null;
}

export function getTileBuildMultiplierVisualLevel(tile: BoardTile): 0 | 1 | 2 | 3 | 4 {
  const buildMultiplier = getTileBuildMultiplier(tile);

  if (buildMultiplier <= 1) {
    return 0;
  }

  if (buildMultiplier === 2) {
    return 1;
  }

  if (buildMultiplier === 3) {
    return 2;
  }

  if (buildMultiplier === 4) {
    return 3;
  }

  return 4;
}

export function getBoardTileAriaLabel(
  index: number,
  tile: BoardTile,
  tilePreview: TilePreview | null
): string {
  const buildMultiplierLabel = getTileBuildMultiplierLabel(tile);
  const multiplierCopy =
    buildMultiplierLabel === null ? "" : ` Scores ${buildMultiplierLabel} when cleared.`;
  const previewCopy = tilePreview === null ? "" : ` ${tilePreview.ariaLabel}`;

  return `Board slot ${index + 1}, value ${tile.value}.${multiplierCopy}${previewCopy}`;
}

export function formatFloatingScoreValue(value: number): string {
  return `+${value.toLocaleString()}`;
}

function moveHintClassName(moveHint: MoveHint): string {
  if (moveHint === "match") {
    return "tile-target-match";
  }

  if (moveHint === "build") {
    return "tile-target-build";
  }

  if (moveHint === "ten-clear") {
    return "tile-target-ten";
  }

  if (moveHint === "negative") {
    return "tile-target-negative";
  }

  if (moveHint === "zero-clear") {
    return "tile-target-zero";
  }

  if (moveHint === "illegal") {
    return "tile-illegal-target";
  }

  return "";
}

function getTilePreview(selectedHandValue: number | null, boardValue: number): TilePreview | null {
  if (selectedHandValue === null) {
    return null;
  }

  const resolution = resolveMove(selectedHandValue, boardValue);
  if (resolution.outcome === "illegal") {
    return null;
  }

  if (resolution.outcome === "match_clear") {
    return {
      label: "MATCH",
      kind: "match",
      risky: false,
      ariaLabel: "Match."
    };
  }

  if (resolution.outcome === "build_to_ten_clear") {
    return {
      label: "CLEAR 10",
      kind: "ten",
      risky: false,
      ariaLabel: "Clear 10."
    };
  }

  if (resolution.outcome === "negative_clear") {
    return {
      label: "CLEAR 0",
      kind: "zero",
      risky: false,
      ariaLabel: "Clear 0."
    };
  }

  if (resolution.outcome === "negative_reduce") {
    return {
      label: `REDUCE ${resolution.builtValue}`,
      kind: "negative",
      risky: (resolution.builtValue ?? 0) >= 8,
      ariaLabel: `Reduce to ${resolution.builtValue}.`
    };
  }

  return {
    label: `BUILD ${resolution.builtValue}`,
    kind: "build",
    risky: (resolution.builtValue ?? 0) >= 8,
    ariaLabel: `Build ${resolution.builtValue}${(resolution.builtValue ?? 0) >= 8 ? ", a risky high tile." : "."}`
  };
}

function getFlowVisualLevel(flowCount: number): 0 | 1 | 2 | 3 | 4 {
  if (flowCount >= 5) {
    return 4;
  }

  if (flowCount === 4) {
    return 3;
  }

  if (flowCount === 3) {
    return 2;
  }

  if (flowCount === 2) {
    return 1;
  }

  return 0;
}

function getBoardStepWidth() {
  if (typeof window === "undefined") {
    return BOARD_STEP_SMALL;
  }

  return window.matchMedia("(min-width: 420px)").matches ? BOARD_STEP_LARGE : BOARD_STEP_SMALL;
}

function floatingScoreToneClassName(kind: ScoreEventType): string {
  if (kind === "match_clear") {
    return "match";
  }

  if (kind === "ten_clear") {
    return "ten";
  }

  return "round";
}

function getFloatingScoreX(floatingScore: FloatingScore, boardStepWidth: number): number {
  if (floatingScore.origin.kind === "tile") {
    return (
      (floatingScore.origin.boardIndex - (floatingScore.origin.boardSize - 1) / 2) * boardStepWidth
    );
  }

  const laneOffset = SCORE_POP_LANE_OFFSETS[floatingScore.lane % SCORE_POP_LANE_OFFSETS.length];
  return laneOffset * 0.7;
}

type ParticleProps = {
  angle: number;
  distance: number;
  color: string;
  delay: number;
  size: number;
};

type FlashProps = {
  color: string;
  intensity: number;
};

type FlowIndicatorProps = {
  timerDisplay: BoardTimerDisplay;
  progress: number;
  flowLevel: number;
  isPulsing: boolean;
  pulseKey: number;
};

function Particle({ angle, distance, color, delay, size }: ParticleProps) {
  return (
    <motion.span
      className="tile-particle"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
        boxShadow: `0 0 ${size + 4}px ${color}`
      }}
      initial={{ opacity: 1, scale: 1, x: 0, y: 0 }}
      animate={{
        opacity: 0,
        scale: 0,
        x: Math.cos((angle * Math.PI) / 180) * distance,
        y: Math.sin((angle * Math.PI) / 180) * distance
      }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
    />
  );
}

function Flash({ color, intensity }: FlashProps) {
  return (
    <motion.span
      className="tile-flash"
      style={{ backgroundColor: color }}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{
        opacity: [0, 0.95, 0],
        scale: [0.85, 1.08 + intensity * 0.03, 1.18 + intensity * 0.04]
      }}
      transition={{ duration: 0.18, times: [0, 0.28, 1], ease: "easeOut" }}
    />
  );
}

function IncrementRay({ angle }: { angle: number }) {
  return (
    <motion.span
      className="tile-increment-ray"
      style={{ rotate: `${angle}deg` }}
      initial={{ opacity: 0.8, scaleX: 0.6, scaleY: 0.7 }}
      animate={{ opacity: 0, scaleX: 1.2, scaleY: 1 }}
      transition={{ duration: 0.26, ease: "easeOut" }}
    />
  );
}

function FlowIndicator({
  timerDisplay,
  progress,
  flowLevel,
  isPulsing,
  pulseKey
}: FlowIndicatorProps) {
  const isFlowTimer = timerDisplay.kind === "flow";
  const isStaticRefill = timerDisplay.kind === "refill" && timerDisplay.expiresAt === null;
  const refillText = "Refill Incoming";

  return (
    <motion.div
      className={`flow-indicator flow-indicator-${timerDisplay.kind}${
        isFlowTimer ? ` flow-indicator-level-${flowLevel}` : ""
      }${isStaticRefill ? " flow-indicator-refill-static" : ""}`}
      initial={{ opacity: 0, y: 8, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.94, transition: { duration: 0.2, ease: "easeOut" } }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      aria-live="polite"
    >
      <div className={`flow-indicator-breathe${isFlowTimer ? "" : " flow-indicator-breathe-refill"}`}>
        <div
          className={`flow-indicator-core flow-indicator-core-${timerDisplay.kind}${
            isFlowTimer && isPulsing ? " flow-indicator-core-pulse" : ""
          }`}
        >
          {isFlowTimer && isPulsing ? (
            <span key={`flow-ring-${pulseKey}`} className="flow-indicator-ring" />
          ) : null}
          <span className="flow-indicator-backdrop" />
          <span className="flow-indicator-meter" aria-hidden="true">
            <span
              className={`flow-indicator-meter-fill flow-indicator-meter-fill-${timerDisplay.kind}`}
              style={{ transform: `scaleX(${progress})` }}
            />
          </span>
          <span className="flow-indicator-text">
            {isFlowTimer ? (
              <>
                <span className="flow-indicator-label">Flow</span>
                <span className="flow-indicator-count">{`X${timerDisplay.flowCount}`}</span>
              </>
            ) : (
              <span className="flow-indicator-copy">{refillText}</span>
            )}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function Board({
  board,
  flowCount,
  incrementLanes,
  timerDisplay,
  timerPausedAt,
  timerSpotlightKind,
  incrementSpotlightTileId,
  floatingScores,
  boardTopOverlay,
  boardRowOverlay,
  selectedHandValue,
  spotlightTileId = null,
  dimNonSpotlightTiles = false,
  dimAllTiles = false,
  showMoveHints = true,
  stabilizeAnimations = false,
  gameOver,
  isResolvingMove,
  pendingMove,
  theme,
  onSelectTile,
  onDismissTimerSpotlight
}: BoardProps) {
  const pulseTimerRef = useRef<number | null>(null);
  const buildBadgePulseTimersRef = useRef(new Map<string, number>());
  const previousBuildMultipliersRef = useRef(new Map<string, number>());
  const buildBadgePulseNonceRef = useRef(0);
  const previousFlowCountRef = useRef(flowCount);
  const lastClearPulseSignatureRef = useRef<string | null>(null);
  const lastPulseAtRef = useRef(0);
  const timerFrameRef = useRef<number | null>(null);
  const [flowPulseKey, setFlowPulseKey] = useState(0);
  const [activeBuildBadgePulses, setActiveBuildBadgePulses] = useState<Record<string, number>>({});
  const [isFlowPulsing, setIsFlowPulsing] = useState(false);
  const [boardStepWidth, setBoardStepWidth] = useState(getBoardStepWidth);
  const [timerNow, setTimerNow] = useState(() => Date.now());
  const [incrementSpotlightStyle, setIncrementSpotlightStyle] = useState<CSSProperties | null>(
    null
  );
  const boardRowRef = useRef<HTMLDivElement | null>(null);
  const disabled = gameOver || isResolvingMove;
  const flowLevel = getFlowVisualLevel(flowCount);
  const showFlow = timerDisplay?.kind === "flow" && !gameOver;
  const showFlowPulse = isFlowActive({ count: flowCount }) && !gameOver;
  const timerSpotlightOpen = timerSpotlightKind !== null;
  const mechanicSpotlightOpen = timerSpotlightOpen || incrementSpotlightTileId !== null;
  const dismissSpotlightButtonHandlers = createInstantPressHandlers<HTMLButtonElement>(
    onDismissTimerSpotlight
  );
  const effectiveTimerNow = timerPausedAt ?? timerNow;
  const hasAnimatedIncrementTimer =
    !gameOver && incrementLanes.some((lane) => lane.expiresAt !== null);
  const incrementLaneByTileId = useMemo(
    () => new Map(incrementLanes.map((lane) => [lane.tileId, lane])),
    [incrementLanes]
  );
  const incrementSpotlightIndex =
    incrementSpotlightTileId === null
      ? -1
      : board.findIndex((tile) => tile.id === incrementSpotlightTileId);
  const incrementSpotlightOpen = incrementSpotlightIndex >= 0;
  const timerProgress =
    timerDisplay === null || timerDisplay.expiresAt === null
      ? 1
      : Math.max(
          0,
          Math.min(1, (timerDisplay.expiresAt - effectiveTimerNow) / timerDisplay.durationMs)
        );

  const queueFlowPulse = (source: "increment" | "clear") => {
    const now = Date.now();
    if (source === "increment" && now - lastPulseAtRef.current < FLOW_PULSE_DURATION_MS) {
      return;
    }

    lastPulseAtRef.current = now;
    setFlowPulseKey((previousKey) => previousKey + 1);
    setIsFlowPulsing(true);

    if (pulseTimerRef.current !== null) {
      window.clearTimeout(pulseTimerRef.current);
    }

    pulseTimerRef.current = window.setTimeout(() => {
      setIsFlowPulsing(false);
      pulseTimerRef.current = null;
    }, FLOW_PULSE_DURATION_MS);
  };

  useEffect(() => {
    if (flowCount > previousFlowCountRef.current && showFlowPulse) {
      queueFlowPulse("increment");
    }

    previousFlowCountRef.current = flowCount;
  }, [flowCount, showFlowPulse]);

  useEffect(() => {
    const pendingOutcome = pendingMove?.outcome ?? null;
    const nextSignature =
      pendingMove === null ? null : `${pendingMove.boardIndex}:${pendingOutcome}`;

    if (
      nextSignature !== null &&
      nextSignature !== lastClearPulseSignatureRef.current &&
      pendingOutcome !== null &&
      CLEAR_OUTCOMES.includes(pendingOutcome) &&
      showFlowPulse
    ) {
      queueFlowPulse("clear");
    }

    lastClearPulseSignatureRef.current = nextSignature;
  }, [pendingMove, showFlowPulse]);

  useEffect(() => {
    const nextBuildMultipliers = new Map<string, number>();
    const increasedTileIds: string[] = [];
    const activeTileIds = new Set<string>();

    for (const tile of board) {
      const buildMultiplier = getTileBuildMultiplier(tile);
      const previousBuildMultiplier = previousBuildMultipliersRef.current.get(tile.id);

      activeTileIds.add(tile.id);
      nextBuildMultipliers.set(tile.id, buildMultiplier);

      if (
        previousBuildMultiplier !== undefined &&
        buildMultiplier > previousBuildMultiplier &&
        buildMultiplier > 1
      ) {
        increasedTileIds.push(tile.id);
      }
    }

    previousBuildMultipliersRef.current = nextBuildMultipliers;

    for (const [tileId, timerId] of buildBadgePulseTimersRef.current.entries()) {
      if (!activeTileIds.has(tileId)) {
        window.clearTimeout(timerId);
        buildBadgePulseTimersRef.current.delete(tileId);
      }
    }

    setActiveBuildBadgePulses((previousPulses) => {
      let nextPulses = previousPulses;

      for (const tileId of Object.keys(previousPulses)) {
        if (!activeTileIds.has(tileId)) {
          if (nextPulses === previousPulses) {
            nextPulses = { ...previousPulses };
          }

          delete nextPulses[tileId];
        }
      }

      if (increasedTileIds.length === 0) {
        return nextPulses;
      }

      if (nextPulses === previousPulses) {
        nextPulses = { ...previousPulses };
      }

      for (const tileId of increasedTileIds) {
        const nextPulseToken = buildBadgePulseNonceRef.current + 1;
        buildBadgePulseNonceRef.current = nextPulseToken;

        nextPulses[tileId] = nextPulseToken;

        const existingTimerId = buildBadgePulseTimersRef.current.get(tileId);
        if (existingTimerId !== undefined) {
          window.clearTimeout(existingTimerId);
        }

        const timeoutId = window.setTimeout(() => {
          setActiveBuildBadgePulses((currentPulses) => {
            if (currentPulses[tileId] !== nextPulseToken) {
              return currentPulses;
            }

            const updatedPulses = { ...currentPulses };
            delete updatedPulses[tileId];
            return updatedPulses;
          });
          buildBadgePulseTimersRef.current.delete(tileId);
        }, BUILD_MULTIPLIER_PULSE_DURATION_MS);

        buildBadgePulseTimersRef.current.set(tileId, timeoutId);
      }

      return nextPulses;
    });
  }, [board]);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current !== null) {
        window.clearTimeout(pulseTimerRef.current);
      }

      for (const timerId of buildBadgePulseTimersRef.current.values()) {
        window.clearTimeout(timerId);
      }
      buildBadgePulseTimersRef.current.clear();

      if (timerFrameRef.current !== null) {
        window.cancelAnimationFrame(timerFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (timerFrameRef.current !== null) {
      window.cancelAnimationFrame(timerFrameRef.current);
      timerFrameRef.current = null;
    }

    if (timerPausedAt !== null) {
      setTimerNow(timerPausedAt);
      return;
    }

    if (
      (timerDisplay === null || timerDisplay.expiresAt === null) &&
      !hasAnimatedIncrementTimer
    ) {
      setTimerNow(Date.now());
      return;
    }

    const tick = () => {
      setTimerNow(Date.now());
      timerFrameRef.current = window.requestAnimationFrame(tick);
    };

    tick();

    return () => {
      if (timerFrameRef.current !== null) {
        window.cancelAnimationFrame(timerFrameRef.current);
        timerFrameRef.current = null;
      }
    };
  }, [hasAnimatedIncrementTimer, timerDisplay?.kind, timerDisplay?.expiresAt, timerPausedAt]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 420px)");
    const syncBoardStepWidth = () => {
      setBoardStepWidth(mediaQuery.matches ? BOARD_STEP_LARGE : BOARD_STEP_SMALL);
    };

    syncBoardStepWidth();

    mediaQuery.addEventListener("change", syncBoardStepWidth);

    return () => {
      mediaQuery.removeEventListener("change", syncBoardStepWidth);
    };
  }, []);

  useLayoutEffect(() => {
    if (!incrementSpotlightOpen || boardRowRef.current === null) {
      setIncrementSpotlightStyle(null);
      return;
    }

    const syncIncrementSpotlightStyle = () => {
      if (boardRowRef.current === null) {
        return;
      }

      const rowRect = boardRowRef.current.getBoundingClientRect();

      setIncrementSpotlightStyle({
        left: `${window.innerWidth / 2}px`,
        top: `${Math.max(INCREMENT_SPOTLIGHT_VIEWPORT_GUTTER + 40, rowRect.top - 10)}px`,
        maxWidth: `${Math.min(
          window.innerWidth - INCREMENT_SPOTLIGHT_VIEWPORT_GUTTER,
          INCREMENT_SPOTLIGHT_MAX_WIDTH
        )}px`
      });
    };

    syncIncrementSpotlightStyle();
    window.addEventListener("resize", syncIncrementSpotlightStyle);
    window.addEventListener("scroll", syncIncrementSpotlightStyle, true);

    return () => {
      window.removeEventListener("resize", syncIncrementSpotlightStyle);
      window.removeEventListener("scroll", syncIncrementSpotlightStyle, true);
    };
  }, [incrementSpotlightOpen, board.length, boardStepWidth, showFlow, timerDisplay?.kind]);

  return (
    <section
      className={`board-panel${showFlow ? " board-panel-flow-active" : ""}${
        isFlowPulsing ? " board-panel-flow-pulsing" : ""
      }${timerSpotlightOpen ? " board-panel-flow-spotlight" : ""}${
        incrementSpotlightOpen ? " board-panel-increment-spotlight" : ""
      }`}
      data-flow-level={showFlow ? flowLevel : 0}
    >
      {mechanicSpotlightOpen ? <div className="flow-spotlight-scrim" aria-hidden="true" /> : null}
      <div
        className={`board-flow-slot${incrementSpotlightOpen ? " board-flow-slot-hidden" : ""}`}
        aria-hidden={incrementSpotlightOpen || (timerDisplay === null && boardTopOverlay == null)}
      >
        <AnimatePresence initial={false}>
          {timerDisplay !== null ? (
            <FlowIndicator
              timerDisplay={timerDisplay}
              progress={timerProgress}
              flowLevel={flowLevel}
              isPulsing={showFlow ? isFlowPulsing : false}
              pulseKey={showFlow ? flowPulseKey : 0}
            />
          ) : null}
        </AnimatePresence>
        {boardTopOverlay}
        <AnimatePresence>
          {timerSpotlightKind !== null ? (
            <div className="flow-spotlight-callout-position">
              <motion.div
                className="flow-spotlight-callout"
                role="dialog"
                aria-modal="true"
                aria-label={timerSpotlightKind === "refill" ? "Refill" : "Flow"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <p className="flow-spotlight-copy">
                  {timerSpotlightKind === "refill" ? (
                    <>
                      Watch out!
                      <br />
                      Empty slots will now refill with more tiles.
                      <br />
                      Maintain flow to prevent it.
                    </>
                  ) : (
                    <>
                      Play tiles quickly to enter flow.
                      <br />
                      Higher flow = higher scores
                    </>
                  )}
                </p>
                <button
                  type="button"
                  className="flow-spotlight-button"
                  {...dismissSpotlightButtonHandlers}
                >
                  OK
                </button>
              </motion.div>
            </div>
          ) : null}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {incrementSpotlightStyle !== null ? (
          <div
            className="tile-spotlight-callout-position tile-spotlight-callout-position-fixed"
            style={incrementSpotlightStyle}
          >
            <motion.div
              className="flow-spotlight-callout"
              role="dialog"
              aria-modal="true"
              aria-label="Incrementing tile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <p className="flow-spotlight-copy">
                This charged tile will gain +1 over time.
                <br />
                Clear it quickly or use it to your advantage.
              </p>
              <button
                type="button"
                className="flow-spotlight-button"
                {...dismissSpotlightButtonHandlers}
              >
                OK
              </button>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
      <div ref={boardRowRef} className={`board-row${gameOver ? " board-row-disabled" : ""}`}>
        {boardRowOverlay}
        <AnimatePresence initial={false}>
          {board.map((tile, index) => {
            const tileColor = getTileColor(tile.value);
            const activeMove = pendingMove?.boardIndex === index ? pendingMove : null;
            const incrementLane = incrementLaneByTileId.get(tile.id) ?? null;
            const incrementRemainingMs =
              incrementLane === null ? 0 : Math.max(0, incrementLane.expiresAt - effectiveTimerNow);
            const incrementUrgency =
              incrementLane === null
                ? 0
                : Math.max(0, 1 - incrementRemainingMs / Math.min(4_000, incrementLane.durationMs));
            const isClearing = !!activeMove && CLEAR_OUTCOMES.includes(activeMove.outcome);
            const isTenClear = activeMove?.outcome === "build_to_ten_clear";
            const isNegativeClear = activeMove?.outcome === "negative_clear";
            const isBuildImpact =
              activeMove?.outcome === "build" || activeMove?.outcome === "negative_reduce";
            const clearIntensity = showFlow ? flowLevel : 0;
            const particleSize = 8 + Math.max(0, clearIntensity - 1);
            const particleDistance = (isTenClear ? 68 : 58) + clearIntensity * 5;
            const targetX = (index - (board.length - 1) / 2) * boardStepWidth;

            const moveHint =
              !showMoveHints || selectedHandValue === null
                ? "none"
                : moveHintFromOutcome(resolveMove(selectedHandValue, tile.value).outcome);
            const tilePreview = showMoveHints
              ? getTilePreview(selectedHandValue, tile.value)
              : null;
            const buildMultiplierLabel = getTileBuildMultiplierLabel(tile);
            const buildMultiplierVisualLevel = getTileBuildMultiplierVisualLevel(tile);
            const buildBadgePulseToken = activeBuildBadgePulses[tile.id] ?? null;
            const isLegalTarget =
              showMoveHints && selectedHandValue !== null && moveHint !== "illegal";
            const isTutorialSpotlight = spotlightTileId === tile.id;
            const isIncrementSpotlight = incrementSpotlightTileId === tile.id;
            const isTutorialSpotlightMuted =
              dimNonSpotlightTiles && spotlightTileId !== null && !isTutorialSpotlight;
            const isIncrementSpotlightMuted = incrementSpotlightOpen && !isIncrementSpotlight;
            const isTileDimmed =
              dimAllTiles || isTutorialSpotlightMuted || isIncrementSpotlightMuted;
            const tileDimOpacity = isIncrementSpotlightMuted ? 0.16 : 0.28;
            const isIncrementImminent = incrementUrgency >= 0.75;
            const flashColor = isTenClear ? "#FFD400" : isNegativeClear ? "#93C5FD" : "#FFFFFF";

            return (
              <motion.button
                key={tile.id}
                type="button"
                initial={
                  stabilizeAnimations ? false : { opacity: 0, scale: 0.92, x: targetX }
                }
                animate={{
                  opacity: isClearing ? 0 : isTileDimmed ? tileDimOpacity : 1,
                  scale: isTenClear ? 1.22 : isClearing ? 1.04 : isLegalTarget ? 1.03 : 1,
                  x: targetX
                }}
                exit={
                  stabilizeAnimations
                    ? { opacity: 0, scale: 1, x: targetX, transition: { duration: 0 } }
                    : { opacity: 0, scale: 0.92, x: targetX, transition: { duration: 0.12 } }
                }
                transition={{
                  x: stabilizeAnimations ? { duration: 0 } : TILE_MOTION_TRANSITION,
                  scale: stabilizeAnimations ? { duration: 0 } : { duration: 0.16, ease: "easeOut" },
                  opacity: { duration: stabilizeAnimations ? 0 : 0.12 }
                }}
                className={`tile tile-board tile-surface-${theme}${
                  isDangerTile(tile.value) ? " tile-danger" : ""
                } ${moveHintClassName(moveHint)}${isLegalTarget ? " tile-legal-target" : ""}${
                  isBuildImpact ? " tile-build-impact" : ""
                }${isTenClear ? " tile-ten-clear tile-special-clear" : ""}${
                  isClearing && !isTenClear ? " tile-clearing" : ""
                }${incrementLane !== null ? " tile-increment-charged" : ""}${
                  isIncrementImminent ? " tile-increment-imminent" : ""
                }${
                  isIncrementSpotlight || isTutorialSpotlight
                    ? " tile-mechanic-spotlight"
                    : ""
                }${isTileDimmed ? " tile-spotlight-muted" : ""}`}
                style={
                  {
                    "--tile-color": tileColor.hex,
                    "--increment-urgency": incrementUrgency.toFixed(3)
                  } as CSSProperties
                }
                onClick={() => onSelectTile(index)}
                disabled={disabled}
                aria-label={getBoardTileAriaLabel(index, tile, tilePreview)}
              >
                {incrementLane !== null ? (
                  <span className="tile-increment-rail" aria-hidden="true">
                    <span className="tile-increment-rail-fill">
                      <span className="tile-increment-rail-cap" />
                    </span>
                  </span>
                ) : null}
                {isClearing ? <Flash color={flashColor} intensity={clearIntensity} /> : null}
                {isClearing &&
                  PARTICLE_ANGLES.map((angle, particleIndex) => (
                    <Particle
                      key={`${tile.id}-particle-${angle}`}
                      angle={angle}
                      distance={particleDistance}
                      color={isTenClear ? "#FFD400" : activeMove?.particleColor ?? tileColor.hex}
                      delay={particleIndex * 0.02}
                      size={particleSize}
                    />
                  ))}
                {incrementLane !== null && incrementLane.burstCount > 0 ? (
                  <>
                    <Flash
                      key={`${tile.id}-increment-flash-${incrementLane.burstCount}`}
                      color="#BFE8FF"
                      intensity={1}
                    />
                    {INCREMENT_RAY_ANGLES.map((angle) => (
                      <IncrementRay
                        key={`${tile.id}-increment-ray-${incrementLane.burstCount}-${angle}`}
                        angle={angle}
                      />
                    ))}
                  </>
                ) : null}
                {tilePreview !== null ? (
                  <span
                    className={`tile-preview-badge tile-preview-badge-${tilePreview.kind}${
                      tilePreview.risky ? " tile-preview-badge-risky" : ""
                    }`}
                  >
                    {tilePreview.label}
                  </span>
                ) : null}
                {buildMultiplierLabel !== null ? (
                  <span
                    className={`tile-build-multiplier-badge${
                      buildBadgePulseToken !== null ? " tile-build-multiplier-badge-pulsing" : ""
                    }`}
                    data-build-level={buildMultiplierVisualLevel}
                    aria-hidden="true"
                  >
                    {buildBadgePulseToken !== null ? (
                      <span
                        key={`${tile.id}-build-badge-burst-${buildBadgePulseToken}`}
                        className="tile-build-multiplier-badge-burst"
                      />
                    ) : null}
                    <span className="tile-build-multiplier-badge-text">
                      {buildMultiplierLabel}
                    </span>
                  </span>
                ) : null}
                <span className="tile-number">{tile.value}</span>
              </motion.button>
            );
          })}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {floatingScores.map((floatingScore) => {
            const targetX = getFloatingScoreX(floatingScore, boardStepWidth);
            const tileLaneLift = floatingScore.lane * -4;
            const startY = floatingScore.origin.kind === "tile" ? -24 + tileLaneLift : 2;
            const peakY = floatingScore.origin.kind === "tile" ? -36 + tileLaneLift : -10;
            const endY = floatingScore.origin.kind === "tile" ? -52 + tileLaneLift : -24;

            return (
              <motion.span
                key={floatingScore.id}
                className={`score-pop score-pop-${floatingScoreToneClassName(
                  floatingScore.kind
                )} score-pop-origin-${
                  floatingScore.origin.kind === "tile" ? "tile" : "center"
                }`}
                initial={{ opacity: 0, x: targetX, y: startY, scale: 0.84 }}
                animate={{
                  opacity: [0, 1, 0],
                  x: targetX,
                  y: [startY, peakY, endY],
                  scale: [0.84, 1.04, 0.96]
                }}
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.12, ease: "easeOut" } }}
                transition={{
                  duration: 0.52,
                  delay: floatingScore.delayMs / 1000,
                  ease: "easeOut",
                  opacity: { times: [0, 0.2, 1] },
                  y: { times: [0, 0.3, 1] },
                  scale: { times: [0, 0.24, 1] }
                }}
              >
                {formatFloatingScoreValue(floatingScore.value)}
              </motion.span>
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
}
